/*!
 * Copyright (c) Trimble Inc. All rights reserved
 */

import { Injectable } from '@angular/core';
import { UserManager, User, UserManagerSettings } from 'oidc-client';
import { Subject, Observable, empty } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

import { UserHandlerService } from './user-handler.service';
import { UserService } from 'api/services';
import { UserMapper } from 'Mappers/UserMapper';
import { environment } from 'src/environments/environment';



@Injectable()
export class AuthentificationService {
  private _userManager: UserManager;
  private _user: User;
  private _loginChangedSubject = new Subject<boolean>();

  loginChanged = this._loginChangedSubject.asObservable();

  private get stsSettings(): UserManagerSettings {
    return {
      authority: environment.issuer,
      client_id: environment.clientId,
      redirect_uri: `${environment.clientRoot}signin-callback`,
      post_logout_redirect_uri:`${environment.clientRoot}signout-callback`,
      scope: `openid ${environment.clientName}`,
      response_type: 'code',
      loadUserInfo: false,
    }
  }
  constructor(private readonly userService: UserService, private readonly userHandler: UserHandlerService) {
    this._userManager = new UserManager(this.stsSettings);
  }
  get getUserManager() {
    return this._userManager;
  }
  get getLoginChangedSubject() {
    return this._loginChangedSubject;
  }
  login() {
    return this.getUserManager.signinRedirect();
  }

  /* istanbul ignore next */
  isLoggedIn(): Promise<boolean> {
   return this._userManager.getUser()
      .then(user => {
        /* istanbul ignore if */
        if (this._user !== user) {
          this._loginChangedSubject.next(this.checkUser(user));
        }
        this._user = user;
         /* istanbul ignore if */
        if (user) {
          this.setUserDetails(user);
        }
        return this.checkUser(user)
      });
  }

  private checkUser = (user: User): boolean => {
    return !!user && !user.expired;
  }


  completeLogin():Promise<User> {
    return this._userManager.signinRedirectCallback().then(user => {
      this._user = user;
      this._loginChangedSubject.next(this.checkUser(user));
      return this.setUserDetails(user);
    });
  }

  setUserDetails(user: User) {
    this.userService.getUserDetails({ userId: user.profile['sub'] })
      .pipe(map(result => {
        return result;
      }), switchMap(currentUser => {
         /* istanbul ignore if */
        if (currentUser === null) {
          const userMapper = new UserMapper();
          return this.userService.addNewUser({ body: userMapper.mapToObject(user) });
        } else {
          this.userHandler.setCurrentUser(currentUser);
          return empty();
        }
      })).subscribe(currentUser => this.userHandler.setCurrentUser(currentUser));
    return user;
  }

  logout() {
    this._userManager.signoutRedirect();
  }

  completeLogout() {
    this._user = null;
    return this._userManager.signoutRedirectCallback();
  }

  public getToken(): string {
    return this._user.access_token;
  }


}
